/**
 * A box you can actually type in.
 *
 * Every field in the conversation editor writes the whole conversation back to
 * the world, and the world answers with the conversation as it now stands. Bound
 * straight to that answer, a text box is rebuilt under the cursor on every
 * keystroke: the caret jumps, the selection goes, and typing a name is a fight.
 *
 * So what is being typed lives here until it is finished with. The value from
 * the world is shown when nothing is being typed, the draft is shown while it
 * is, and the draft is handed over when the box is left, on Enter in a single
 * line, or after a pause long enough to mean somebody has stopped. Escape puts
 * back what was there.
 *
 * A field can ask to wait for the box to be left and nothing else, which is
 * what anything with consequences beyond itself wants.
 */

import { useEffect, useRef, useState } from 'react';

/** How long a pause counts as having stopped typing. */
const SETTLED_MS = 700;

export function Written({
  value,
  onDone,
  limit = 400,
  rows,
  clean,
  placeholder,
  className,
  onlyOnLeaving,
}: {
  value: string;
  onDone: (text: string) => void;
  /** The most it will hold, which is whatever the world will keep. */
  limit?: number;
  /** Given, it is a textarea of that many rows. Without, a single line. */
  rows?: number;
  /** Takes out whatever this field will not have, for a name or an id. */
  clean?: (text: string) => string;
  placeholder?: string;
  className?: string;
  /**
   * Whether to wait for the box to be left rather than for a pause in typing.
   *
   * For a name. Renaming a node rewrites every wire that pointed at the old
   * name, so doing it halfway through a word means the conversation is rebuilt
   * around a name nobody meant, and again on the next pause. A pause is not a
   * decision; leaving the box is.
   */
  onlyOnLeaving?: boolean;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);

  // Once the world says back what was typed, the draft has nothing left to say
  // and gets out of the way. Dropped on handing it over instead, the box would
  // show the old words again for the fraction of a second the save is in
  // flight, which is the same flicker in a slower disguise.
  if (draft !== null && draft === value) setDraft(null);

  const shown = draft ?? value;

  /**
   * The latest of everything the handing over needs, for the moments it happens
   * outside a render.
   *
   * What a field does with its words is written as an arrow function where the
   * field is used, so it is a different function on every render. Waiting on
   * that function meant the pause timer was thrown away and started again every
   * time anything at all redrew, and while a quest is being followed something
   * redraws twice a second: the pause never arrived and nothing was ever saved.
   */
  const latest = useRef({ draft, value, onDone });
  useEffect(() => {
    latest.current = { draft, value, onDone };
  });

  const handOver = (): void => {
    const held = latest.current;
    if (held.draft !== null && held.draft !== held.value) held.onDone(held.draft);
  };

  // Handed over once somebody has stopped. Without this a name typed and then
  // left alone without leaving the box would sit there unsaved, which is a
  // worse surprise than a jumping caret.
  useEffect(() => {
    if (onlyOnLeaving || draft === null || draft === value) return;

    const timer = window.setTimeout(handOver, SETTLED_MS);
    return () => window.clearTimeout(timer);
    // Only what actually changes the answer. Redrawing is not typing.
  }, [draft, value, onlyOnLeaving]);

  /**
   * And handed over if the box goes away with words still in it.
   *
   * Pressing another node takes this box out of the page, and a box that is no
   * longer there never hears that it was left: without this, everything typed
   * since the last pause went with it.
   */
  useEffect(() => () => handOver(), []);

  const typed = (text: string): void => {
    setDraft((clean ? clean(text) : text).slice(0, limit));
  };

  const done = (): void => {
    handOver();
  };

  const keys = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    if (event.key === 'Escape') {
      setDraft(null);
      event.currentTarget.blur();
      return;
    }

    // Enter finishes a name. In a textarea it is a new line, which is what
    // somebody writing a speech means by it.
    if (event.key === 'Enter' && rows === undefined) {
      event.preventDefault();
      event.currentTarget.blur();
    }
  };

  if (rows !== undefined) {
    return (
      <textarea
        className={className ?? 'editor-text'}
        rows={rows}
        value={shown}
        placeholder={placeholder}
        onChange={(event) => typed(event.target.value)}
        onBlur={done}
        onKeyDown={keys}
      />
    );
  }

  return (
    <input
      type="text"
      className={className}
      value={shown}
      placeholder={placeholder}
      onChange={(event) => typed(event.target.value)}
      onBlur={done}
      onKeyDown={keys}
    />
  );
}
